/**
 * 相机环境自检面板（仅在 `?camera-debug=1` 时渲染，学员永远看不到）
 * -----------------------------------------------------------------------------
 * 存在的理由：**真机上的相机问题没法靠猜**。
 *   - 微信里连不上远程调试，`console` 看不到；
 *   - 「拍不了」可能是安全上下文、可能是不支持取流、也可能是权限被拒，
 *     三者的处置完全不同（换 https / 换入口 / 教用户开权限）。
 *
 * 所以给一个能在手机上打开、能截图的东西：UA、判定结论、以及**实际调一次流**
 * 拿到的 `error.name`。`error.name` 是唯一无法从 UA 推断出来的事实。
 *
 * 用法：任意页面 URL 后加 `?camera-debug=1`（本工程是 `BrowserRouter`，
 * 查询串就在 `window.location.search` 上），例如
 *   `https://<域名>/enroll/xxx?camera-debug=1`
 *
 * 一旦问题定位完，删掉这个文件与 `App.tsx` 里的一行挂载即可 —— 它不承载任何业务。
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { detectCameraEnv } from '../lib/camera-env';

type ProbeState =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'ok'; width: number; height: number }
  | { kind: 'error'; name: string; message: string };

export function CameraEnvProbe() {
  const { search } = useLocation();
  const [open, setOpen] = useState(true);
  const [probe, setProbe] = useState<ProbeState>({ kind: 'idle' });
  // 用路由上的 search 而不是 `window.location.search`：面板要能跟着导航即时收放
  if (new URLSearchParams(search).get('camera-debug') !== '1' || !open) return null;

  const env = detectCameraEnv();

  const test = async () => {
    setProbe({ kind: 'requesting' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings();
      setProbe({ kind: 'ok', width: settings?.width ?? 0, height: settings?.height ?? 0 });
      for (const item of stream.getTracks()) item.stop();
    } catch (error) {
      // error.name 才是关键：NotAllowedError=权限被拒、
      // NotFoundError=无摄像头、NotReadableError=被占用、SecurityError=非安全上下文
      setProbe({
        kind: 'error',
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const rows: Array<[string, string]> = [
    ['判断', env.canLiveCapture ? '可实时取景' : `降级系统相机（${env.reason}）`],
    ['isSecureContext', String(env.secureContext)],
    ['mediaDevices.getUserMedia', env.hasGetUserMedia ? '存在' : '缺失'],
    ['微信', env.isWechat ? '是' : '否'],
    ['平台', env.isIos ? 'iOS' : env.isAndroid ? 'Android' : '其它'],
    ['UA', env.userAgent],
  ];

  return (
    <div className="camera-probe">
      <div className="camera-probe__head">
        <strong>相机环境自检</strong>
        <button type="button" className="camera-probe__close" onClick={() => setOpen(false)}>
          关闭
        </button>
      </div>
      <dl className="camera-probe__list">
        {rows.map(([key, value]) => (
          <div key={key} className="camera-probe__row">
            <dt>{key}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <button type="button" className="camera-probe__test" onClick={() => void test()}>
        实际调用一次相机
      </button>
      {probe.kind === 'requesting' ? (
        <p className="camera-probe__result">正在请求…</p>
      ) : probe.kind === 'ok' ? (
        <p className="camera-probe__result camera-probe__result--ok">
          成功：拿到 {probe.width}×{probe.height} 视频流
        </p>
      ) : probe.kind === 'error' ? (
        <p className="camera-probe__result camera-probe__result--error">
          {probe.name}：{probe.message}
        </p>
      ) : null}
    </div>
  );
}
